import { DependencyContainer, InjectionToken, Lifecycle, container } from "@launchtray/tsyringe-async";
import { ICanvasScaler } from "../../canvas/ICanvasScaler";
import { SceneManager, Viewable } from "scene-manager";
import { InjectableInterfaces } from "./InjectableInterfaces";
import { PreloaderScene } from "../scenes/preloader/PreloaderScene";
import { PreloaderController } from "../scenes/preloader/controllers/PreloaderContorller";
import { PreloaderModel } from "../scenes/preloader/models/PreloaderModel";
import { BundleResourcesPackage, IPackageRegistrator } from "resource-system";
import { GameScene } from "../scenes/game/GameScene";
import { MenuScene } from "../scenes/menu/MenuScene";
import { InjectableFactories } from "./InjectableFactories";
import { SettingsScene } from "../scenes/popups/settings/SettinsScene";
import { MenuController } from "../scenes/menu/controllers/MenuController";
import { LocalStorage } from "../../variableSaver/LocalStorage";
import { VariableSaver } from "../../variableSaver/VariableSaver";
import { VictoryScene } from "../scenes/popups/victory/VictoryScene";
import { LoseScene } from "../scenes/popups/lose/LoseScene";
import { GameController } from "../scenes/game/controllers/GameController";
import { RenderCamera } from "../three/RenderCamera";
import { FontResourcesPackage } from "../common/resources/FontResourcesPackage";
import { LocalizationModel } from "../common/models/LocalizationModel";
import { PauseScene } from "../scenes/popups/pause/PauseScene";
import { PlayerModel } from "../common/models/PlayerModel";
import { SaveModel } from "../common/models/SaveModel";
import { GameViewModel } from "../scenes/game/models/GameViewModel";
import { VictorySceneOptions } from "../scenes/popups/victory/VictorySceneOptions";
import { LoseSceneOptions } from "../scenes/popups/lose/LoseSceneOptions";
import { VictoryPopupController } from "../scenes/popups/victory/controllers/VictoryPopupController";
import { Renderer } from "pixi.js";
import { AudioController } from "../scenes/game/components/AudioController";
import { SettingsModel } from "../common/models/SettingsModel";
import { SettingsController } from "../common/controllers/SettingsController";
import { GameModel } from "../scenes/game/models/GameModel";
import { LevelModel } from "../common/models/LevelModel";
import { AdScene } from "../scenes/popups/ad/AdScene";
import { BoostersConfiguration } from "../common/models/BoostersConfiguration";
import { TutorialModel } from "../common/models/TutorialModel";
import { ForceRotateScene } from "../scenes/popups/rotate/ForceRotateScene";
import { GameSceneOptions } from "../scenes/game/GameSceneOptions";
import { GameDistributionSdkWrapper } from "../gamedistribution/GameDistributionSdkWrapper";
import { DummySdkWrapper } from "../gamedistribution/DummySdkWrapper";
import { IGameDistributionSdk } from "../gamedistribution/interfaces/IGameDistributionSdk";
import { TickerModel } from "../common/models/TickerModel";
import { BackgroundResourcesPackage } from "../common/resources/BackgroundResourcesPackage";

export class PeskyMolesContext implements IPackageRegistrator {
    private readonly internalContainer: DependencyContainer;

    public constructor() {
        this.internalContainer = container.createChildContainer();
    }

    public build(
        canvasScaler: ICanvasScaler,
        sceneManager: SceneManager,
        renderCamera: RenderCamera,
        renderer: Renderer
    ): void {
        const sdkImplementation = this.selectAdSdk(window.location.href);
        this.internalContainer.registerSingleton<IGameDistributionSdk>(InjectableInterfaces.IGameDistributionSdk, sdkImplementation);

        this.internalContainer.registerSingleton(TickerModel, TickerModel);
        this.internalContainer.registerSingleton(AudioController, AudioController);
        this.internalContainer.registerSingleton(InjectableInterfaces.IDataStorage, LocalStorage);
        this.internalContainer.registerSingleton(VariableSaver, VariableSaver);
        this.internalContainer.registerSingleton(BackgroundResourcesPackage, BackgroundResourcesPackage);
        this.internalContainer.registerSingleton(FontResourcesPackage, FontResourcesPackage);

        this.internalContainer.registerSingleton(LocalizationModel, LocalizationModel);
        this.internalContainer.registerSingleton(SaveModel, SaveModel);
        this.internalContainer.registerSingleton(PlayerModel, PlayerModel);
        this.internalContainer.registerSingleton(TutorialModel, TutorialModel);
        this.internalContainer.registerSingleton(LevelModel, LevelModel);
        this.internalContainer.registerSingleton(BoostersConfiguration, BoostersConfiguration);
        this.internalContainer.registerSingleton(SettingsModel, SettingsModel);

        this.internalContainer.registerInstance(InjectableInterfaces.ICanvasScaler, canvasScaler);
        this.internalContainer.registerInstance(SceneManager, sceneManager);
        this.internalContainer.registerInstance(InjectableInterfaces.IPackageRegistrator, this);
        this.internalContainer.registerInstance(RenderCamera, renderCamera);
        this.internalContainer.registerInstance(Renderer, renderer);

        this.internalContainer.register(SettingsController, SettingsController, { lifecycle: Lifecycle.ResolutionScoped });

        this.internalContainer.register(PreloaderScene, PreloaderScene);
        this.internalContainer.register(PreloaderController, PreloaderController, { lifecycle: Lifecycle.ResolutionScoped });
        this.internalContainer.register(PreloaderModel, PreloaderModel, { lifecycle: Lifecycle.ResolutionScoped });

        this.internalContainer.register(MenuScene, MenuScene);
        this.registerSceneFactory(InjectableFactories.MenuFactory, MenuScene);
        this.internalContainer.register(MenuController, MenuController, { lifecycle: Lifecycle.ResolutionScoped });

        this.internalContainer.register(SettingsScene, SettingsScene);
        this.registerSceneFactory(InjectableFactories.SettingsFactory, SettingsScene);

        this.internalContainer.register(GameScene, GameScene);
        this.registerSceneFactory(InjectableFactories.GameFactory, GameScene, async (options: GameSceneOptions) => {
            return await this.resolveWithParams(this.internalContainer, GameScene, new ParamDescription(InjectableInterfaces.GameSceneOptions, options));
        });
        this.internalContainer.register(GameModel, GameModel, { lifecycle: Lifecycle.ResolutionScoped });
        this.internalContainer.register(GameViewModel, GameViewModel, { lifecycle: Lifecycle.ResolutionScoped });
        this.internalContainer.register(GameController, GameController, { lifecycle: Lifecycle.ResolutionScoped });

        this.internalContainer.register(PauseScene, PauseScene);
        this.registerSceneFactory(InjectableFactories.PauseFactory, PauseScene);

        this.internalContainer.register(VictoryScene, VictoryScene);
        this.registerSceneFactory(
            InjectableFactories.VictoryFactory,
            VictoryScene,
            async (options: VictorySceneOptions) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    VictoryScene,
                    new ParamDescription(InjectableInterfaces.VictorySceneOptions, options)
                );
            }
        );
        this.internalContainer.register(VictoryPopupController, VictoryPopupController, { lifecycle: Lifecycle.ResolutionScoped });

        this.internalContainer.register(LoseScene, LoseScene);
        this.registerSceneFactory(
            InjectableFactories.LoseFactory,
            LoseScene,
            async (options: LoseSceneOptions) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    LoseScene,
                    new ParamDescription(InjectableInterfaces.LoseSceneOptions, options)
                );
            }
        );

        // Ad Popup
        this.internalContainer.register(AdScene, AdScene);
        this, this.internalContainer.register(InjectableFactories.AdFactory, {
            useValue: async () => {
                return await this.internalContainer.resolve(AdScene);
            }
        });

        // Force Rotate Popup
        this.internalContainer.register(ForceRotateScene, ForceRotateScene);
        this, this.internalContainer.register(InjectableFactories.ForceRotateFactory, {
            useValue: async () => {
                return await this.internalContainer.resolve(ForceRotateScene);
            }
        });
    }

    public async resolve<T>(token: InjectionToken<T>): Promise<T> {
        return await this.internalContainer.resolve(token);
    }

    public registerPackage(pkgType: new () => BundleResourcesPackage, pkg: BundleResourcesPackage): void {
        this.internalContainer.registerInstance(pkgType, pkg);
    }

    private registerSceneFactory<TScene extends Viewable<TResult>, TResult>(factoryToken: InjectableFactories, sceneToken: InjectionToken<TScene>, factory?: (...args: any[]) => Promise<TScene>): void {
        this.internalContainer.register(factoryToken, {
            useValue: factory != null ? factory : async () => await this.internalContainer.resolve(sceneToken)
        });
    }

    private async resolveWithParams<T>(
        container: DependencyContainer,
        token: InjectionToken<T>,
        ...args: Array<ParamDescription<any>>
    ): Promise<T> {
        const tempContainer = container.createChildContainer();
        for (const arg of args) {
            tempContainer.registerInstance(arg.token, arg.instance);
        }
        const instance = await tempContainer.resolve(token);
        tempContainer.clearInstances();
        tempContainer.reset();
        return instance;
    }

    private selectAdSdk(host: string): new (...args: any[]) => IGameDistributionSdk {
        if (host.includes("localhost") || host.includes("127.0.0.1") || host.includes("gamefactory.by")) {
            return DummySdkWrapper;
        }
        return GameDistributionSdkWrapper;
    }
}

class ParamDescription<T> {
    public readonly token: InjectionToken<T>;
    public readonly instance: T;

    public constructor(token: InjectionToken<T>, instance: T) {
        this.token = token;
        this.instance = instance;
    }
}